import React, {useContext, useEffect} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import DropdownList from "patient-ping-remedy/packages/dropdown-list";
import { Value } from 'patient-ping-remedy/packages/dropdown-list/lib/DropdownList';
import {useRosterStore} from '@/store/roster_store';
import {css} from "@emotion/css";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {router} from "@/router";
import {SearchParam} from "@/helpers/search_params";
import RosterHelpers from "@/helpers/roster_helpers";
import {FacilityPickerWrapper} from "@/components/header/FacilityPicker.styles";

const ALL_ROSTERS_VALUE = 'all';

const FacilityPicker = () => {
  const { currentRoster, setCurrentRoster } = useRosterStore();
  const { carecoApi } = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const rosterId = searchParams.get(SearchParam.ROSTER_ID);

  const { data: rosterOptions, isLoading } = useQuery({
    queryKey: ['rosterOptions'],
    queryFn: () => carecoApi?.getRosterOptions(),
    enabled: !!carecoApi
  });

  const { data: rosters } = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi
  });

  useEffect(() => {
    if (!rosters?.rosterOptions) {
      return;
    }

    const roster = rosterId
      ? RosterHelpers.getRosterById(rosters, rosterId)
      : currentRoster;

    if (roster) {
      setCurrentRoster(roster);
      if (!searchParams.has(SearchParam.ROSTER_ID)) {
        handleSearchParamSideEffect(roster.id, true);
      }
    } else {
      if (searchParams.has(SearchParam.ROSTER_ID)) {
        searchParams.delete(SearchParam.ROSTER_ID);
        setSearchParams(searchParams);
      }
      handleNavigateSideEffect();
    }
  }, [rosters, rosterId, currentRoster, location.pathname]);

  const handleChange = (val : Value | null) => {
    if (val?.value === ALL_ROSTERS_VALUE) {
      setCurrentRoster(null);
      queryClient.removeQueries({queryKey: ['appliedFilters']});
      if (searchParams.has(SearchParam.ROSTER_ID)) {
        searchParams.delete(SearchParam.ROSTER_ID);
        setSearchParams(searchParams, { replace: true });
      }
      handleNavigateSideEffect();
      return;
    }
    const roster = RosterHelpers.getRosterById(rosters, val?.value);
    setCurrentRoster(roster ?? null);
    queryClient.removeQueries({queryKey: ['appliedFilters']});
    handleNavigateSideEffect();
    handleSearchParamSideEffect(roster?.id, !searchParams.has(SearchParam.ROSTER_ID));
  };

  const handleNavigateSideEffect = () => {
    if (/^\/clients\/profile(\/.*)?$/.test(location.pathname)) {
      router.navigate('/clients');
    }
  };

  const handleSearchParamSideEffect = (rosterId?: number, replace?: boolean) => {
    if (/^\/(clients|referrals|network_registry)(\/details)?$/.test(location.pathname)) {
      if (rosterId && searchParams.get(SearchParam.ROSTER_ID) !== `${rosterId}`) {
        searchParams.set(SearchParam.ROSTER_ID, `${rosterId}`);
        setSearchParams(searchParams, { replace });
      }
    }
  };

  const isMessagesRoute = /^\/messages(\/|$)/.test(location.pathname);
  const isNotificationsRoute = /^\/notifications(\/|$)/.test(location.pathname);
  const isAllGroupsRoute = isMessagesRoute || isNotificationsRoute;
  const noSelectionLabel = isAllGroupsRoute ? 'All' : 'Select a group';
  const ALL_OPTION: Value = { label: 'All', value: ALL_ROSTERS_VALUE };
  const dropDownItems = isAllGroupsRoute
    ? [ALL_OPTION, ...(rosterOptions ?? [])]
    : rosterOptions ?? [];

  return (
    <FacilityPickerWrapper>
      <DropdownList
        className={css({ height: '4rem', width: '25rem', zIndex: '2' })}
        items={dropDownItems}
        value={{
          label: currentRoster?.name ?? noSelectionLabel,
          value: currentRoster?.id ? currentRoster.id.toString() : ALL_ROSTERS_VALUE
        }}
        handleChange={handleChange}
        id={'facility-picker'}
        isLoading={isLoading}
        noOptionsMessage={() => isLoading ? 'Loading groups...' : 'No groups found'}
        placeholder={noSelectionLabel}
      />
    </FacilityPickerWrapper>
  );
};

export default FacilityPicker;
